"use client";

import { GenericButton } from "@capex/components/general/button.component";
import { Spinner, Input } from "@material-tailwind/react";
import { useTransition, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { sendOTP, signupUser } from "@capex/services/apiQueries/user";
import { revalidateRetailPlansPath } from "@capex/services/revalidations";
import { SignupData } from "@capex/types";
import { useRouter } from "next/navigation";
import OtpInput from "react-otp-input";
import Link from "next/link";
import validator from "validator";
import { EyeOff, Eye } from "lucide-react";

export default function SignupPage() {
  const [stage, setStage] = useState<number>(1);
  const [isPending, startTransition] = useTransition();
  const [signUpData, setSignUpData] = useState<SignupData>({
    firstName: "",
    lastName: "",
    email: "",
    bvn: "",
    nin: "",
    phoneNo: "",
    dateOfBirth: "",
  });
  const [showBvn, setShowBvn] = useState(false);
  const [showNin, setShowNin] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: signUpData,
  });

  const onSubmit = handleSubmit(async (data) => {
    startTransition(async () => {
      setSignUpData(data);
      try {
        const res = await sendOTP({
          phone: data.phoneNo,
          email: data.email,
          firstName: data.firstName,
        });
        if (res === "An error occurred") {
          toast.error("An error occurred, please try again");
        } else {
          setStage(2);
        }
      } catch (error) {
        toast.error("An unexpected error occurred.");
      }
    });
  });

  const millisecondsInEighteenYears = 1000 * 60 * 60 * 24 * 365.25 * 18; // (considering leap years)
  const Timestamp18YearsAgo =
    new Date().getTime() - millisecondsInEighteenYears;

  return (
    <>
      {stage === 1 && (
        <div className="flex justify-center items-center w-full h-full">
          <div className="flex flex-col items-center gap-8 w-full">
            <p className="font-semibold text-3xl text-primary">Sign Up</p>
            <form
              onSubmit={onSubmit}
              className="flex flex-col items-center gap-8 w-full"
            >
              <div className="flex flex-col items-center gap-[18px] w-full md:w-[450px]">
                <div className="flex flex-col gap-1 w-full">
                  <Input
                    variant="static"
                    color="black"
                    placeholder="First Name"
                    {...register("firstName", {
                      required: true,
                      minLength: 2,
                      maxLength: 40,
                    })}
                    {...({} as any)}
                  />
                  {errors?.firstName && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors?.firstName?.type === "minLength" &&
                        "First Name must contain at least 2 characters"}
                      {errors.firstName?.type === "maxLength" &&
                        "First Name must contain maximum of 40 characters"}
                      {errors.firstName?.type === "required" &&
                        "First Name is required"}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <Input
                    variant="static"
                    color="black"
                    placeholder="Surname"
                    {...register("lastName", {
                      required: true,
                      minLength: 2,
                      maxLength: 40,
                    })}
                    {...({} as any)}
                  />
                  {errors?.lastName && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors?.lastName?.type === "minLength" &&
                        "Surname must contain at least 2 characters"}
                      {errors.lastName?.type === "maxLength" &&
                        "Surname must contain maximum of 40 characters"}
                      {errors.lastName?.type === "required" &&
                        "Surname is required"}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <Input
                    variant="static"
                    color="black"
                    placeholder="Email Address"
                    {...register("email", {
                      required: true,
                      validate: (value) => validator.isEmail(value),
                    })}
                    {...({} as any)}
                  />
                  {errors?.email && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors.email?.type === "required" && "Email is required"}
                      {errors.email?.type === "validate" &&
                        "Email must be valid"}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 relative w-full">
                  <Input
                    type={showBvn ? "text" : "password"}
                    variant="static"
                    color="black"
                    placeholder="BVN"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    {...register("bvn", {
                      required: true,
                      minLength: 11,
                      maxLength: 11,
                      validate: (value) => validator.isNumeric(value ?? ""),
                      onChange: (e) => {
                        // Sanitize input: remove any non-digit characters
                        e.target.value = e.target.value.replace(/\D/g, "");
                      },
                    })}
                    {...({} as any)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowBvn(!showBvn)}
                    className="absolute top-4 right-1 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={showBvn ? "Hide BVN" : "Show BVN"}
                  >
                    {showBvn ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  {errors?.bvn && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors?.bvn?.type === "minLength" &&
                        "BVN must contain 11 digits"}
                      {errors.bvn?.type === "maxLength" &&
                        "BVN must contain 11 digits"}
                      {errors.bvn?.type === "required" && "BVN is required"}
                      {errors.bvn?.type === "validate" &&
                        "BVN characters must be digits"}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 relative w-full">
                  <Input
                    type={showNin ? "text" : "password"}
                    variant="static"
                    color="black"
                    placeholder="NIN"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    {...register("nin", {
                      required: true,
                      minLength: 11,
                      maxLength: 11,
                      validate: (value) => validator.isNumeric(value ?? ""),
                      onChange: (e) => {
                        e.target.value = e.target.value.replace(/\D/g, "");
                      },
                    })}
                    {...({} as any)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNin(!showNin)}
                    className="absolute top-4 right-1 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={showNin ? "Hide NIN" : "Show NIN"}
                  >
                    {showNin ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  {errors?.nin && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors?.nin?.type === "minLength" &&
                        "NIN must contain 11 digits"}
                      {errors.nin?.type === "maxLength" &&
                        "NIN must contain 11 digits"}
                      {errors.nin?.type === "required" && "NIN is required"}
                      {errors.nin?.type === "validate" &&
                        "NIN characters must be digits"}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <Input
                    variant="static"
                    color="black"
                    placeholder="Phone Number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    {...register("phoneNo", {
                      required: true,
                      validate: (value) =>
                        validator.isMobilePhone(value ?? "", "en-NG"),
                      onChange: (e) => {
                        e.target.value = e.target.value.replace(/\D/g, "");
                      },
                    })}
                    {...({} as any)}
                  />
                  {errors?.phoneNo && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors.phoneNo?.type === "required" &&
                        "Phone Number is required"}
                      {errors.phoneNo?.type === "validate" &&
                        "Phone Number must be valid"}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <Input
                    variant="static"
                    label="Date of birth (above 18)"
                    labelProps={{
                      className: "!text-text-placeholder !text-xs mt-2",
                    }}
                    placeholder="dd/mm/yyyy"
                    className="text-text-dark"
                    color="black"
                    type="date"
                    {...register("dateOfBirth", {
                      required: true,
                      validate: (value) =>
                        //@ts-ignore
                        new Date(value) !== "Invalid Date" &&
                        //@ts-ignore
                        new Date(value).getTime() < Timestamp18YearsAgo,
                    })}
                    {...({} as any)}
                  />
                  {errors?.dateOfBirth && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors.dateOfBirth?.type === "validate" &&
                        "Date of Birth must be a validate date and you must be above 18 years"}
                      {errors.dateOfBirth?.type === "required" &&
                        "Date of Birth is required"}
                    </p>
                  )}
                </div>
                <GenericButton
                  type="submit"
                  className="bg-secondary mt-8 !w-full"
                >
                  {isPending ? (
                    <div className="flex justify-center items-center">
                      <Spinner className="h-6 w-6" {...({} as any)} />
                    </div>
                  ) : (
                    "Create Account"
                  )}
                </GenericButton>
                <p className="text-text-mid text-base font-normal mt-1">
                  Already have an account?
                  <Link href={"/auth/signin"}>
                    <span className="text-text-blue_accent font-medium text-[17px] ml-2">
                      Sign In
                    </span>
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      )}
      {stage === 2 && <SignupOTPverificationComponent formData={signUpData} />}
    </>
  );
}

const SignupOTPverificationComponent = ({
  formData,
}: {
  formData: SignupData;
}) => {
  const [otp, setOtp] = useState<string>("");
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);

  const handleOTPVerification = async () => {
    setLoading(true);
    try {
      const res = await signupUser(formData);
      if (res.statusCode === "Created") {
        const response = await fetch("/api/auth/signin", {
          method: "POST",
          body: JSON.stringify({
            email: formData.email,
            firstName: res.firstName,
            lastName: res.lastName,
            phoneNumber: formData.phoneNo,
            otp,
          }),
        });
        if (response.ok) {
          startTransition(async () => {
            await revalidateRetailPlansPath();
            router.push("/console");
          });
        } else {
          setLoading(false);
          toast.error("An error occured");
        }
      } else if (
        res.statusMessage ===
        "Client with email or phone number provided already exist!"
      ) {
        setLoading(false);
        toast.error("User already exists");
      } else {
        setLoading(false);
        toast.error("An error occurred");
      }
    } catch (error) {
      setLoading(false);
      toast.error("An unexpected error occurred.");
    }
  };
  return (
    <div className="h-full grid grid-cols-1 place-content-center text-center">
      <div className="mb-8">
        <p className="font-semibold text-3xl text-primary">OTP Verification</p>
      </div>

      <OtpInput
        value={otp}
        onChange={setOtp}
        numInputs={6}
        renderInput={(props) => <input {...props} />}
        containerStyle="flex justify-center items-center gap-2 md:gap-4 w-full mb-8"
        inputStyle="!w-[45px] md:!w-[72px] h-[40px] md:h-[60px] text-lg md:text-2xl font-medium text-text-dark border-[1px] border-[#D9D9D9] rounded-[8px] focus:outline-none focus:border-primary"
      />

      <div className="w-full md:w-[450px] mx-auto md:mt-3 mb-5">
        <GenericButton
          className="bg-secondary mx-auto !w-full cursor-pointer disabled:bg-disabled"
          disabled={otp.length !== 6}
          onClick={handleOTPVerification}
        >
          {isPending || loading ? (
            <div className="flex justify-center items-center gap-2">
              <Spinner className="w-6 h-6" {...({} as any)} />
              <p>Verifying</p>
            </div>
          ) : (
            "Verify"
          )}
        </GenericButton>
      </div>

      <p className="text-text-mid">
        Didn&apos;t receive OTP code?
        <span className="font-medium text-primary cursor-pointer ml-2 text-[17px]">
          Resend Now
        </span>
      </p>
    </div>
  );
};
